import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useModelConfig } from '../../hooks/useModelConfigs';

/**
 * The retired Model Config pages' URLs (task 1167). Model Configs now live
 * inline under their Model on the Models page, so every old address lands
 * there; `replace` keeps the old URL out of history. `/llm-configs/*` still
 * maps to these first (`LegacyModelConfigsRedirect`, task 1074).
 */

/** The page the three redirects land on, with a Model expanded when one is known. */
export function modelsPagePath(modelRowId?: string | null, providerKey?: string | null, addConfig = false): string {
  const params = new URLSearchParams();
  if (providerKey) params.set('provider', providerKey);
  if (modelRowId) params.set('model', modelRowId);
  if (addConfig && modelRowId) params.set('addConfig', '1');
  const query = params.toString();
  return `/model-catalog${query ? `?${query}` : ''}`;
}

/** `/model-configs` → the Models page. `?modelId=` (the old Model filter) expands that Model. */
export function ModelConfigsListRedirect() {
  const [searchParams] = useSearchParams();
  return <Navigate to={modelsPagePath(searchParams.get('modelId'))} replace />;
}

/**
 * `/model-configs/new` → the Models page. With `?modelId=` (the review's "Add
 * config") the Model opens expanded with Add config on it.
 */
export function NewModelConfigRedirect() {
  const [searchParams] = useSearchParams();
  const modelRowId = searchParams.get('modelId');
  return <Navigate to={modelsPagePath(modelRowId, null, !!modelRowId)} replace />;
}

/**
 * `/model-configs/:id` → the Models page with the config's Model expanded,
 * best effort: the config read names its Model and provider. A config that
 * cannot be read lands on the page root.
 */
export function ModelConfigDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  const { data: config, isLoading, isError } = useModelConfig(id);
  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-gray-500 dark:text-gray-400">Opening Models…</div>;
  }
  if (isError || !config) return <Navigate to={modelsPagePath()} replace />;
  return <Navigate to={modelsPagePath(config.modelId, config.model?.provider?.key ?? null)} replace />;
}
